import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useSession } from "../lib/session";
import { PageHeading, Alert, Spinner, Empty, StatusPill } from "../components/ui";

export default function Positions() {
  const { user } = useSession();
  return user?.role === "INDUSTRY" ? <IndustryPositions /> : <PositionBrowser />;
}

/* ---------------- Students, staff, coordinators ---------------- */

function PositionBrowser() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.get<{ positions: any[] }>("/positions"),
  });
  const applications = useQuery({
    queryKey: ["applications", "me"],
    queryFn: () => api.get<{ applications: any[] }>("/students/me/applications"),
    enabled: user?.role === "STUDENT",
  });
  // Students land here instead of a dashboard, so this page carries the
  // reminder that used to be the dashboard's checklist. Same query key as the
  // EOI form, so the two share one cache entry and the banner clears as soon
  // as the student submits.
  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<{ submissionsOpen: boolean }>("/config"),
    staleTime: Infinity,
  });
  const profile = useQuery({
    queryKey: ["student", "me"],
    queryFn: () => api.get<{ profile: any }>("/students/me"),
    enabled: user?.role === "STUDENT",
  });

  const apply = useMutation({
    mutationFn: (positionId: string) => api.post("/students/me/applications", { positionId }),
    onSuccess: () => {
      setMessage({ tone: "success", text: "Application submitted." });
      queryClient.invalidateQueries({ queryKey: ["applications", "me"] });
    },
    onError: (err) => setMessage({ tone: "error", text: err instanceof ApiError ? err.message : "Could not apply" }),
  });

  if (isLoading) return <Spinner />;
  const appliedIds = new Set(applications.data?.applications.map((a) => a.position.id) ?? []);
  const positions = data?.positions ?? [];

  // Not just a nag: POST /students/me/applications refuses an unsubmitted EOI,
  // so this is what stands between the student and the Apply buttons below.
  const eoi = profile.data?.profile;
  const blocked = user?.role === "STUDENT" && !profile.isLoading && !eoi?.submittedAt;

  return (
    <>
      <PageHeading title="Internship positions" description="Positions approved and published by the internship coordinator." />
      {blocked && (
        <Alert>
          {!config?.submissionsOpen
            ? <>Submissions open soon. Fill in your expression of interest now so it is ready.{" "}
                <Link className="underline" to="/profile">{eoi ? "Review it" : "Start it now"}</Link>.</>
            : <>{eoi
                ? "Your expression of interest is saved but not submitted, so you cannot apply yet."
                : "You have not started your expression of interest, so you cannot apply yet."}{" "}
                <Link className="underline" to="/profile">{eoi ? "Review and submit it" : "Start it now"}</Link>.</>}
        </Alert>
      )}
      {message && <Alert tone={message.tone}>{message.text}</Alert>}

      {positions.length === 0 ? (
        <Empty>No positions have been published yet.</Empty>
      ) : (
        <ul className="space-y-4">
          {positions.map((p) => (
            <li key={p.id} className="card">
              <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-heading">{p.title}</h2>
                  <p className="text-sm text-uoa-grey">
                    {p.company.name}
                    {p.location ? ` · ${p.location}` : ""}
                    {p.paid ? " · Paid" : " · Unpaid"}
                    {p.hoursPerWeek ? ` · ${p.hoursPerWeek} hrs/week` : ""}
                  </p>
                </div>
                {user?.role !== "STUDENT" && <StatusPill status={p.status} />}
              </div>

              <p className="mb-4 whitespace-pre-wrap text-sm">{p.description}</p>

              <div className="flex flex-wrap items-center gap-3 text-sm text-uoa-grey">
                {p.closesAt && <span>Closes {new Date(p.closesAt).toLocaleDateString("en-NZ")}</span>}
                {typeof p._count?.applications === "number" && user?.role !== "STUDENT" && (
                  <span>{p._count.applications} applicant{p._count.applications === 1 ? "" : "s"}</span>
                )}
                {user?.role === "STUDENT" &&
                  (appliedIds.has(p.id) ? (
                    <span className="font-medium text-green-700">Applied</span>
                  ) : (
                    <button className="btn-primary" onClick={() => apply.mutate(p.id)} disabled={apply.isPending}>
                      Apply
                    </button>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/* ---------------- Industry: manage own postings ---------------- */

function IndustryPositions() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["company", "me"],
    queryFn: () => api.get<{ company: any }>("/companies/me"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["company", "me"] });
  const onError = (err: unknown) => setError(err instanceof ApiError ? err.message : "Something went wrong");

  const create = useMutation({
    mutationFn: (body: unknown) => api.post("/positions", body),
    onSuccess: () => { setCreating(false); setError(null); invalidate(); },
    onError,
  });
  const submit = useMutation({ mutationFn: (id: string) => api.post(`/positions/${id}/submit`), onSuccess: invalidate, onError });
  const close = useMutation({ mutationFn: (id: string) => api.post(`/positions/${id}/close`), onSuccess: invalidate, onError });

  if (isLoading) return <Spinner />;
  const company = data?.company;

  if (company?.status !== "APPROVED") {
    return (
      <>
        <PageHeading title="Your positions" />
        <Alert tone="error">Your organisation is awaiting approval. You can post positions once approved.</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeading title="Your positions" description="Drafts are reviewed by an internship coordinator before publication." />
      {error && <Alert tone="error">{error}</Alert>}

      {creating ? (
        <form
          className="card mb-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            create.mutate({
              title: f.get("title"), description: f.get("description"), location: f.get("location"),
              paid: f.get("paid") === "on",
              hoursPerWeek: f.get("hoursPerWeek") ? Number(f.get("hoursPerWeek")) : undefined,
              startDate: f.get("startDate"), endDate: f.get("endDate"), answers: {},
            });
          }}
        >
          <div>
            <label className="label" htmlFor="title">Position title <span aria-hidden className="text-red-700">*</span></label>
            <input id="title" name="title" required className="input" />
          </div>
          <div>
            <label className="label" htmlFor="description">Description <span aria-hidden className="text-red-700">*</span></label>
            <textarea id="description" name="description" required rows={6} className="input" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="location">Location</label>
              <input id="location" name="location" className="input" />
            </div>
            <div>
              <label className="label" htmlFor="hoursPerWeek">Hours per week</label>
              <input id="hoursPerWeek" name="hoursPerWeek" type="number" min={1} max={60} className="input" />
            </div>
            <div>
              <label className="label" htmlFor="startDate">Start date</label>
              <input id="startDate" name="startDate" className="input" placeholder="e.g. Nov 2026" />
            </div>
            <div>
              <label className="label" htmlFor="endDate">End date</label>
              <input id="endDate" name="endDate" className="input" placeholder="e.g. Feb 2027" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="paid" defaultChecked /> This is a paid internship
          </label>
          {/* TODO: remaining industry position fields, pending the client's field list. */}
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={create.isPending}>Save draft</button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </form>
      ) : (
        <button className="btn-primary mb-6" onClick={() => setCreating(true)}>Post a position</button>
      )}

      {company.positions?.length === 0 ? (
        <Empty>You have not posted any positions yet.</Empty>
      ) : (
        <ul className="space-y-4">
          {company.positions.map((p: any) => (
            <li key={p.id} className="card">
              <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                <h2 className="text-lg font-semibold text-heading">{p.title}</h2>
                <StatusPill status={p.status} />
              </div>
              <p className="mb-4 line-clamp-3 text-sm text-uoa-grey">{p.description}</p>
              <div className="flex flex-wrap gap-3">
                {p.status === "DRAFT" && (
                  <button className="btn-primary" onClick={() => submit.mutate(p.id)} disabled={submit.isPending}>
                    Submit for approval
                  </button>
                )}
                {p.status === "PUBLISHED" && (
                  <>
                    {/* The student directory, scoped to this position -- the
                        same table, filters and sorting as browsing everyone,
                        rather than a second cut-down list of its own. */}
                    <Link className="btn-secondary" to={`/students?position=${p.id}`}>
                      View applicants
                      {typeof p._count?.applications === "number" && ` (${p._count.applications})`}
                    </Link>
                    <button className="btn-secondary" onClick={() => close.mutate(p.id)} disabled={close.isPending}>Close</button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

